import { createContext, useCallback, useContext, useMemo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import { useTeams, type Team } from '@/api-models/teams';
import { useAuth } from '@/contexts/auth-context';

interface SpaceContextValue {
  /** The active team id from the URL (`/app/t/:teamId`), or the user's personal team. */
  teamId: string;
  /** The resolved active team, once the teams list has loaded. */
  activeTeam: Team | undefined;
  setActiveTeam: (team: Team) => void;
  teams: Team[];
  mutateTeams: () => void;
  /** True until the teams list has been fetched, so `activeTeam` can be trusted. */
  isLoadingTeams: boolean;
}

const SpaceContext = createContext<SpaceContextValue | null>(null);

/** Map a team id to a team-scoped URL prefix. */
export function teamPath(teamId: string, rest = ''): string {
  return `/app/t/${teamId}${rest}`;
}

export function SpaceProvider({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  const { teams, mutate, isLoading } = useTeams();
  const navigate = useNavigate();

  // The URL is the source of truth for the active team; fall back to the
  // user's real personal team (no sentinel) when the URL is unscoped.
  const match = useMatch('/app/t/:teamId/*');
  const teamId = match?.params.teamId ?? me.personalTeamId;

  const activeTeam = useMemo(() => teams.find((t) => t.id === teamId), [teams, teamId]);

  const setActiveTeam = useCallback(
    (team: Team) => {
      navigate(teamPath(team.id, '/sessions'));
    },
    [navigate],
  );

  const value = useMemo(
    () => ({ teamId, activeTeam, setActiveTeam, teams, mutateTeams: mutate, isLoadingTeams: isLoading }),
    [teamId, activeTeam, setActiveTeam, teams, mutate, isLoading],
  );

  return <SpaceContext.Provider value={value}>{children}</SpaceContext.Provider>;
}

export function useSpace() {
  const ctx = useContext(SpaceContext);
  if (!ctx) throw new Error('useSpace must be used within SpaceProvider');
  return ctx;
}

/** What the current user is allowed to see in the active space. */
export function useSpaceAccess() {
  const { activeTeam, isLoadingTeams } = useSpace();
  return {
    isPersonal: !!activeTeam?.is_personal,
    isAdmin: activeTeam?.role === 'owner' || activeTeam?.role === 'admin',
    isResolved: !isLoadingTeams && !!activeTeam,
  };
}
